import { Controller } from "@hotwired/stimulus"

// FD-1188: keep the main content pane's scroll position across Back/Forward and
// same-page reloads. FD-691 (sidebar_scroll_controller.js) is the precedent.
//
// The body is overflow-hidden and pages scroll inside the layout's overflow-y-auto pane.
// Turbo only saves and restores WINDOW scroll (always 0 here), so Back/Forward always
// landed at the top of the pane, and the layout's `scroll: :preserve` never covered it
// (a morph keeps the pane only because idiomorph reuses the element). A surface with its
// own full-height scroller inside the pane marks it data-main-scroll-target="scroller",
// and that element is the one saved and restored instead.
//
// Unlike the sidebar, which keeps one offset for every page, this offset belongs to a
// page. It is saved in sessionStorage per page key (see pageKey) on scroll (throttled)
// and just before Turbo leaves, caches or re-renders the page. A freshly rendered pane
// restores it only when:
//   - the visit is a restoration (Back/Forward: turbo:visit detail.action "restore"),
//     where the saved offset wins even over a #fragment, or
//   - the SAME page was drawn again without a morph, and not by a link click: a form's
//     redirect back to a URL Turbo's stale location didn't recognise (FD-1187).
// Anything else (a link to a page, a first load) starts at the top, so an #anchor wins.
//
// A morph keeps this element, so connect() never runs and nothing saved is restored over
// it. turbo:render's detail.renderMethod can't be the test: a frame-promoted visit
// (opening the peek) reports "replace" while replacing nothing. One morph case does need
// help: a frame drawn back to its placeholder (the item page's activity feed) briefly
// shortens the page, the browser clamps the offset, and it stays clamped after the frame
// reloads. onMorph puts back the offset read just before that render, never a saved one.
//
// Limitations: lazy turbo-frames can leave a new page too short for its saved offset,
// and the browser clamps it. We retry as frames inside the scroller load, for
// RETRY_WINDOW_MS; content that grows later stays clamped. (The same window applies after
// a morph, so a morph that really did shorten the page can be put back down if a frame
// inside it loads in that time.) Keys are URLs, not history entries, so two entries for
// one URL share an offset. A full browser reload or a Back from another site restores
// nothing (no Turbo visit to read).
const STORAGE_KEY = "fd:main-scroll"
const MAX_PAGES = 50 // bound the stored map; Back rarely reaches further
const SAVE_DELAY_MS = 150
const RETRY_WINDOW_MS = 2000

// Path + query. The #fragment is left out: it is not part of the page (redirect_back
// loses it, FD-1187), and the anchor wins on arrival anyway. So is ?peek: the peek
// drawer floats over the same list, which keeps one offset whether or not it is open.
function pageKey(href) {
  const url = new URL(href, window.location.href)
  if (url.searchParams.has("peek")) url.searchParams.delete("peek")
  return url.pathname + url.search
}

// The visit in flight, recorded on turbo:visit: it fires before the new body renders,
// and the controller instance that will connect for that body does not exist yet.
let visitSerial = 0
let pendingVisit = null
let lastClickUrl = null

document.addEventListener("turbo:click", (event) => {
  lastClickUrl = event.detail.url
})
document.addEventListener("turbo:visit", (event) => {
  const { url, action } = event.detail
  visitSerial += 1
  // History has not changed yet for a non-restore visit, so location is still the page
  // being left. (A restore has already moved it, but a restore never needs fromKey.)
  pendingVisit = { action, fromKey: pageKey(window.location.href), clicked: url === lastClickUrl }
  lastClickUrl = null
})
document.addEventListener("turbo:load", () => {
  pendingVisit = null
})

function shouldRestore(visit, key) {
  if (!visit) return false // a full page load: the browser (and any #anchor) decides
  if (visit.action === "restore") return true
  return visit.fromKey === key && !visit.clicked && !anchorTargetExists()
}

function anchorTargetExists() {
  const hash = window.location.hash.slice(1)
  if (hash === "") return false
  try {
    return document.getElementById(decodeURIComponent(hash)) !== null
  } catch (_e) {
    return document.getElementById(hash) !== null // a malformed %-escape
  }
}

function readPositions() {
  try {
    const positions = JSON.parse(sessionStorage.getItem(STORAGE_KEY))
    return positions?.constructor === Object ? positions : {}
  } catch (_e) {
    return {}
  }
}

function writePosition(key, top) {
  const positions = readPositions()
  delete positions[key] // re-insert so the newest page sorts last and the oldest trims first
  positions[key] = Math.round(top)
  const keys = Object.keys(positions)
  keys.slice(0, Math.max(0, keys.length - MAX_PAGES)).forEach((old) => delete positions[old])
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions))
  } catch (_e) {
    // sessionStorage can throw (private mode / quota). Scroll memory is a nicety, so
    // fail silently rather than interfere with navigation.
  }
}

export default class extends Controller {
  static targets = ["scroller"]

  connect() {
    this.key = pageKey(window.location.href)
    this.restoreSerial = null
    this.save = this.save.bind(this)
    this.onScroll = this.onScroll.bind(this)
    this.onLoad = this.onLoad.bind(this)
    this.onBeforeRender = this.onBeforeRender.bind(this)
    this.onMorph = this.onMorph.bind(this)
    // Capture: scroll doesn't bubble, and the scroller may be a descendant.
    this.element.addEventListener("scroll", this.onScroll, { capture: true, passive: true })
    document.addEventListener("turbo:before-visit", this.save)
    document.addEventListener("turbo:before-cache", this.save)
    document.addEventListener("turbo:before-render", this.onBeforeRender)
    document.addEventListener("turbo:morph", this.onMorph)
    document.addEventListener("turbo:load", this.onLoad)

    if (shouldRestore(pendingVisit, this.key)) {
      this.restoreSerial = visitSerial
      this.restore()
    }
  }

  disconnect() {
    clearTimeout(this.saveTimer)
    this.saveTimer = null
    this.cancelRetry()
    this.element.removeEventListener("scroll", this.onScroll, { capture: true })
    document.removeEventListener("turbo:before-visit", this.save)
    document.removeEventListener("turbo:before-cache", this.save)
    document.removeEventListener("turbo:before-render", this.onBeforeRender)
    document.removeEventListener("turbo:morph", this.onMorph)
    document.removeEventListener("turbo:load", this.onLoad)
  }

  get scroller() {
    return this.hasScrollerTarget ? this.scrollerTarget : this.element
  }

  onScroll(event) {
    if (event.target !== this.scroller) return // a menu, a table, the peek drawer...
    // A scroll away from where a pending retry left the pane is the user's: they win.
    if (this.retry && Math.abs(this.scroller.scrollTop - this.retry.applied) > 1) this.cancelRetry()
    if (this.saveTimer) return
    this.saveTimer = setTimeout(this.save, SAVE_DELAY_MS)
  }

  onBeforeRender() {
    this.save()
    this.topBeforeRender = this.scroller.scrollTop
  }

  // This pane survived a morph. If the morph clamped its offset (see the header), put back
  // the offset it had just before, as the frames reload.
  onMorph() {
    if (this.topBeforeRender - this.scroller.scrollTop > 1) this.applyOffset(this.topBeforeRender)
  }

  onLoad() {
    // Turbo scrolls to a URL's #anchor after rendering, so restore once more after it.
    if (this.restoreSerial === visitSerial) this.restore()
    this.restoreSerial = null
    // A morph can move to another URL (same path, new query) without reconnecting.
    this.key = pageKey(window.location.href)
  }

  save() {
    clearTimeout(this.saveTimer)
    this.saveTimer = null
    // While a retry waits for the page to grow, keep the saved target, not the clamp.
    if (this.retry) return
    writePosition(this.key, this.scroller.scrollTop)
  }

  restore() {
    const top = readPositions()[this.key]
    if (Number.isFinite(top)) this.applyOffset(top)
  }

  // Sets the offset; if the page is still too short and the browser clamped it, tries
  // again as turbo-frames inside the scroller load, until it fits or RETRY_WINDOW_MS ends.
  applyOffset(top) {
    this.cancelRetry()
    const scroller = this.scroller
    scroller.scrollTop = top
    if (top - scroller.scrollTop <= 1) return

    const onFrameLoad = (event) => {
      if (!scroller.contains(event.target)) return
      scroller.scrollTop = top
      if (top - scroller.scrollTop <= 1) this.cancelRetry()
      else if (this.retry) this.retry.applied = scroller.scrollTop
    }
    const timer = setTimeout(() => this.cancelRetry(), RETRY_WINDOW_MS)
    document.addEventListener("turbo:frame-load", onFrameLoad)
    this.retry = {
      applied: scroller.scrollTop,
      cancel: () => {
        clearTimeout(timer)
        document.removeEventListener("turbo:frame-load", onFrameLoad)
      }
    }
  }

  cancelRetry() {
    this.retry?.cancel()
    this.retry = null
  }
}
